import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { addOwnEntities } from '../ManageListingsPage/ManageListingsPage.duck';

// Re-export everything from ManageListingsPage.duck.js since we're using the same functionality
export * from '../ManageListingsPage/ManageListingsPage.duck';

// ================ Action types ================ //

export const UPDATE_STOCK_REQUEST = 'app/CataloguePage/UPDATE_STOCK_REQUEST';
export const UPDATE_STOCK_SUCCESS = 'app/CataloguePage/UPDATE_STOCK_SUCCESS';
export const UPDATE_STOCK_ERROR = 'app/CataloguePage/UPDATE_STOCK_ERROR';
export const UPDATE_PRICE_REQUEST = 'app/CataloguePage/UPDATE_PRICE_REQUEST';
export const UPDATE_PRICE_SUCCESS = 'app/CataloguePage/UPDATE_PRICE_SUCCESS';
export const UPDATE_PRICE_ERROR = 'app/CataloguePage/UPDATE_PRICE_ERROR';

// ================ Action creators ================ //

export const updateStockRequest = listingId => ({ type: UPDATE_STOCK_REQUEST, payload: { listingId } });
export const updateStockSuccess = response => ({ type: UPDATE_STOCK_SUCCESS, payload: response.data });
export const updateStockError = error => ({ type: UPDATE_STOCK_ERROR, payload: error, error: true });
export const updatePriceRequest = listingId => ({ type: UPDATE_PRICE_REQUEST, payload: { listingId } });
export const updatePriceSuccess = response => ({ type: UPDATE_PRICE_SUCCESS, payload: response.data });
export const updatePriceError = error => ({ type: UPDATE_PRICE_ERROR, payload: error, error: true });

// ================ Thunks ================ //

/**
 * Update stock for a listing using SDK's compareAndSet method
 */
export const updateListingStock = (listingId, oldTotal, newTotal) => (dispatch, getState, sdk) => {
  dispatch(updateStockRequest(listingId));

  return sdk.stock
    .compareAndSet({ listingId, oldTotal, newTotal }, { expand: true })
    .then(response => {
      // Update marketplace entities with the new stock data
      dispatch(addMarketplaceEntities(response));
      dispatch(addOwnEntities(response));
      dispatch(updateStockSuccess(response));
      return response;
    })
    .catch(e => {
      log.error(e, 'catalogue-update-stock-failed', { listingId, oldTotal, newTotal });
      dispatch(updateStockError(storableError(e)));
      throw e;
    });
};

/**
 * Update listing base price from catalogue inline editor.
 */
export const updateListingPrice = (listingId, newPrice) => (dispatch, getState, sdk) => {
  dispatch(updatePriceRequest(listingId));

  const state = getState();
  const listingEntity = state?.ManageListingsPage?.ownEntities?.ownListing?.[listingId];
  const listingAttributes = listingEntity?.attributes;
  const listingPublicData = listingAttributes?.publicData || {};
  const listingPrice = listingAttributes?.price;
  const listingCurrency = listingPrice?.currency;

  if (!listingEntity?.id || !listingCurrency || typeof newPrice !== 'number' || Number.isNaN(newPrice)) {
    const invalidInputError = new Error('Invalid listing or price payload for updateListingPrice');
    dispatch(updatePriceError(storableError(invalidInputError)));
    return Promise.reject(invalidInputError);
  }

  const { Money } = sdkTypes;
  const priceInSubUnits = convertUnitToSubUnit(newPrice, unitDivisor(listingCurrency));
  const updatedPrice = new Money(priceInSubUnits, listingCurrency);

  return sdk.ownListings
    .update(
      {
        id: listingEntity.id,
        price: updatedPrice,
        // ownListings.update replaces publicData object, so keep existing keys.
        publicData: listingPublicData,
      },
      { expand: true, include: ['images', 'currentStock'] }
    )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(addOwnEntities(response));
      dispatch(updatePriceSuccess(response));
      return response;
    })
    .catch(e => {
      log.error(e, 'catalogue-update-price-failed', { listingId, newPrice });
      dispatch(updatePriceError(storableError(e)));
      throw e;
    });
};
